import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user

  useEffect(() => {
    let active = true;

    // Handle Emergent Google OAuth callback (session_id in URL fragment) FIRST.
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      api
        .post("/auth/google/session", { session_id: sid })
        .then((res) => {
          if (!active) return;
          setUser(res.data.user);
          window.history.replaceState({}, document.title, "/admin");
        })
        .catch((e) => {
          if (!active) return;
          setUser(false);
          toast.error(formatApiError(e.response?.data?.detail) || "Google sign-in failed");
          window.history.replaceState({}, document.title, "/admin/login");
        });
      return () => { active = false; };
    }

    // Validate existing session (JWT via localStorage token or Google session cookie).
    api
      .get("/auth/me")
      .then((res) => active && setUser(res.data))
      .catch(() => {
        localStorage.removeItem("wv_token");
        if (active) setUser(false);
      });
    return () => { active = false; };
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("wv_token", data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem("wv_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, loginWithGoogle, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
